import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface SystemMetric {
  timestamp: string;
  cpu: number;
  memory: number;
  apiLatency: number;
  errorRate: number;
}

const data: SystemMetric[] = [
  { timestamp: '00:00', cpu: 45, memory: 62, apiLatency: 42, errorRate: 0.1 },
  { timestamp: '04:00', cpu: 42, memory: 58, apiLatency: 38, errorRate: 0.2 },
  { timestamp: '08:00', cpu: 48, memory: 65, apiLatency: 45, errorRate: 0.1 },
  { timestamp: '12:00', cpu: 52, memory: 70, apiLatency: 48, errorRate: 0.3 },
  { timestamp: '16:00', cpu: 55, memory: 72, apiLatency: 52, errorRate: 0.2 },
  { timestamp: '20:00', cpu: 47, memory: 64, apiLatency: 44, errorRate: 0.1 },
];

const CustomXAxis = (props: any) => (
  <XAxis
    {...props}
    stroke="hsl(var(--muted-foreground))"
    fontSize={12}
    tickLine={false}
  />
);

const CustomYAxis = (props: any) => (
  <YAxis
    {...props}
    stroke="hsl(var(--muted-foreground))"
    fontSize={12}
    tickLine={false}
  />
);

export function SystemHealth() {
  const currentStatus = {
    cpu: data[data.length - 1].cpu,
    memory: data[data.length - 1].memory,
    apiLatency: data[data.length - 1].apiLatency,
    errorRate: data[data.length - 1].errorRate,
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>System Health</CardTitle>
        <div className="flex gap-2">
          <Badge variant="outline">
            CPU: {currentStatus.cpu}%
          </Badge>
          <Badge variant="outline">
            Memory: {currentStatus.memory}%
          </Badge>
          <Badge variant="outline">
            API Latency: {currentStatus.apiLatency}ms
          </Badge>
          <Badge variant={currentStatus.errorRate < 0.2 ? 'default' : 'destructive'}>
            Error Rate: {currentStatus.errorRate}%
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <CustomXAxis dataKey="timestamp" />
              <CustomYAxis />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload?.length) {
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-sm">
                        <div className="text-sm font-medium">{label}</div>
                        <div className="mt-1 space-y-1">
                          {payload.map((entry) => (
                            <div
                              key={entry.name}
                              className="flex items-center gap-2 text-sm"
                            >
                              <div
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: entry.color }}
                              />
                              <span className="text-muted-foreground">
                                {entry.name}: {entry.value}
                                {entry.name === 'API Latency' ? 'ms' : 
                                  entry.name === 'Error Rate' ? '%' : '%'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="cpu"
                name="CPU Usage"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="memory"
                name="Memory Usage"
                stroke="hsl(var(--warning))"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="apiLatency"
                name="API Latency"
                stroke="hsl(var(--success))"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="errorRate"
                name="Error Rate"
                stroke="hsl(var(--destructive))"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}